import * as _ from 'lodash';

import ngRedux from 'ng-redux';

import {
    Store,
    StoreElement,
    IActionStoreService,
    IAlarmCategoriesStoreService,
    IAlarmEmailStoreService,
    IAlarmStoreService,
    IAlarmTemplateStoreService,
    IApiInformationStoreService,
    IApplicationStoreService,
    IApprovalSettingsStoreService,
    IApprovalStrategyStoreService,
    IAuthenticationStoreService,
    ICategoryStoreService,
    IConnectionPointStoreService,
    IDashboardStoreService,
    IDashboardUserStoreService,
    IDigitalCorrespondanceTableStoreService,
    IDigitalSetStoreService,
    IGlobalNotificationsStoreService,
    IGroupsStoreService,
    IHistorianStorageConfigurationStoreService,
    IIdentifiersStoreService,
    ILayoutStoreService,
    IMailingJobsStoreService,
    IObjectAccessRightsStoreService,
    IUserGroupsStoreService,
    IOnlineUsersStoreService,
    IStatesStoreService,
    IStoreItem,
    ITagStoreService,
    ITaskStoreService,
    ITemplatesStoreService,
    ITicketStatusStoreService,
    ITicketStoreService,
    ITicketTypesStoreService,
    ITriggerActionStoreService,
    ITriggerStoreService,
    IUserStoreService,
    IValidationPageConfigurationStoreService,
    IValueViewModelStoreService,
    IVariablesStoreService,
} from '../stateManagement';
import { GuidHelper } from '../wtFramework';
import { ILoggerService } from './general';
import { ICurrentApplicationService } from './data';
import { Application } from '../models';
import { IStoreActionsService } from './storeActions.service';

export type IStoreCallback = (store: Store) => void;

export interface IStoreDispatchers {
    actions: IActionStoreService;
    alarmCategories: IAlarmCategoriesStoreService;
    alarmEmails: IAlarmEmailStoreService;
    alarms: IAlarmStoreService;
    alarmTemplates: IAlarmTemplateStoreService;
    apiInformation: IApiInformationStoreService;
    application: IApplicationStoreService;
    approvalStrategy: IApprovalStrategyStoreService;
    approvalSettings: IApprovalSettingsStoreService;
    authentication: IAuthenticationStoreService;
    categories: ICategoryStoreService;
    connectionPoints: IConnectionPointStoreService;
    dashboards: IDashboardStoreService;
    dashboardUsers: IDashboardUserStoreService;
    digitalSets: IDigitalSetStoreService;
    digitalCorrespondanceTables: IDigitalCorrespondanceTableStoreService;
    globalNotifications: IGlobalNotificationsStoreService;
    groups: IGroupsStoreService;
    historianStorageConfiguration: IHistorianStorageConfigurationStoreService;
    identifiers: IIdentifiersStoreService;
    layout: ILayoutStoreService;
    mailingJobs: IMailingJobsStoreService;
    objectAccessRights: IObjectAccessRightsStoreService;
    userGroups: IUserGroupsStoreService;
    onlineUsers: IOnlineUsersStoreService;
    states: IStatesStoreService;
    tags: ITagStoreService;
    tasks: ITaskStoreService;
    templates: ITemplatesStoreService;
    tickets: ITicketStoreService;
    ticketStatuses: ITicketStatusStoreService;
    ticketTypes: ITicketTypesStoreService;
    triggers: ITriggerStoreService;
    triggerActions: ITriggerActionStoreService;
    validationPageConfigurations: IValidationPageConfigurationStoreService;
    valueViewModels: IValueViewModelStoreService;
    variables: IVariablesStoreService;
    users: IUserStoreService;
}

export interface IStoreService {
    register(callback: IStoreCallback, elements: StoreElement[]): () => void;
    registerAsync(sync?: IStoreRegisteredCallback, async?: IStoreRegisteredCallback): () => void;
    dispatchers(): IStoreDispatchers;
}

export interface IStoreRegisteredCallback {
    callback: IStoreCallback;
    elements: StoreElement[];
}

type RegisteredCallbacks = { [guid: string]: IStoreRegisteredCallback };

export class StoreService implements IStoreService {
    static serviceId = 'storeService';

    private _syncRegistered: RegisteredCallbacks = {};
    private _asyncRegistered: RegisteredCallbacks = {};
    private _dispatchers: IStoreDispatchers;
    private _lastStore: Store;

    static $inject = [
        '$ngRedux',
        'storeActionsService',
        // Regular service
        'currentApplicationService',
        'loggerService'
    ];

    constructor(
        private _$ngRedux: ngRedux.INgRedux,
        private _storeActionsService: IStoreActionsService,
        // Regular services
        private _currentApplicationService: ICurrentApplicationService,
        private _loggerService: ILoggerService) {
        this.suscribeRedux();
    }

    // Simple synced registering
    register(callback: IStoreCallback, elements: StoreElement[]): () => void {
        return this.registerAsync({ callback, elements });
    }

    // Allow to register both sync & async callbacks
    registerAsync(
        sync?: IStoreRegisteredCallback,
        async?: IStoreRegisteredCallback
    ): () => void {
        const guid = GuidHelper.getGuid();

        if (sync) {
            this._syncRegistered[guid] = this.registerStoreCallback(sync);
        }

        if (async) {
            this._asyncRegistered[guid] = this.registerStoreCallback(async);
        }

        // return callback to remove registration
        return () => {
            delete this._syncRegistered[guid];
            delete this._asyncRegistered[guid];
        };
    }

    dispatchers(): IStoreDispatchers {
        return this._dispatchers;
    }

    // Add a callback to the pile and and trigger loading of direct call if already loaded
    private registerStoreCallback(register: IStoreRegisteredCallback): IStoreRegisteredCallback {
        // copy registration (to prevent in memory change on our back)
        const registered = { callback: register.callback, elements: register.elements };
        // first call if store is ready, or start loading
        if (this._lastStore && this.isLoaded(registered, this._lastStore, false)) {
            this.callRegistered(registered, this._lastStore);
        } else {
            // modern loading
            this._loadWithDispatcher(registered.elements);
        }
        return registered;
    }

    private suscribeRedux() {
        this._$ngRedux.connect((store: Store) => {
            const modified = this.getStoreModifiedElements(store);
            this._lastStore = store;
            // call each callback according to ask
            this.callEachRegistered(this._syncRegistered, modified, store, false);
            this.callEachRegistered(this._asyncRegistered, modified, store, true);
            // don't care to bind any properties to myself anyway
            return {};
        }, dispatch => {
            return {
                _dispatchers: this._storeActionsService.init(dispatch),
            };
        })(this);
    }

    // Track store change to determine changes
    private getStoreModifiedElements(store: Store): StoreElement[] {
        const elements = StoreElement;
        const values = Object.keys(elements).map(k => elements[k]);
        const result = _.filter(values, e => this._lastStore && store[e] !== this._lastStore[e]);
        return result;
    }

    // Call all registered callback impacted by element changes
    private callEachRegistered(
        allRegistered: RegisteredCallbacks,
        modified: StoreElement[],
        store: Store,
        async: boolean
    ) {
        Object
            .keys(allRegistered)
            .map(key => allRegistered[key])
            // only affected & maybe loaded element
            .filter(r => this.isImpacted(r, modified) && (async || this.isLoaded(r, store)))
            // call them
            .forEach(r => this.callRegistered(r, store));
    }

    // Test if a registered callback is impateced by store changes
    private isImpacted(registered: IStoreRegisteredCallback, modified: StoreElement[]): boolean {
        return _.some(registered.elements, e => modified.indexOf(e) >= 0);
    }

    // Test if a registered callback is loaded
    private isLoaded(registered: IStoreRegisteredCallback, store: Store, hasWarning = true): boolean {
        return _.every(registered.elements, e => {
            const item = (store[e] as IStoreItem);
            let result = false;
            if (!item) {
                this._loggerService.logWarning(`Element ${e} not found.`, false);
            } else if (hasWarning && !item.isLoaded && !item.isLoading) {
                this._loggerService.logWarning(`Store service has some callback for store element ${e}, but store does not seems to be loading it anymore.`, false);
                this._loggerService.logWarning(item as any, false);
            } else {
                result = item.isLoaded && !item.hasError;
            }
            return result;
        });
    }

    // Safe call a registered callback
    private callRegistered(registered: IStoreRegisteredCallback, store: Store): void {
        try {
            registered.callback(store);
        }
        catch (e) {
            this._loggerService.logWarning('There was an error when calling store callback from StoreService.');
            this._loggerService.logWarning(e);
        }
    }


    // Next-gen, should be filled when dispatcher are created
    private _loadWithDispatcher(elements: StoreElement[]): void {
        const dictionary: { [storeElement: string]: () => void; } = {};
        dictionary[StoreElement.Actions] = () => {
            this._dispatchers.actions.getAll();
        };
        dictionary[StoreElement.AlarmCategories] = () => {
            this._dispatchers.alarmCategories.getAll();
        };
        dictionary[StoreElement.Alarms] = () => {
            this._dispatchers.alarms.getAll();
        };
        dictionary[StoreElement.AlarmEmails] = () => {
            this._dispatchers.alarmEmails.getAll();

        };
        dictionary[StoreElement.AlarmTemplates] = () => {
            this._dispatchers.alarmTemplates.getAll();
        };
        dictionary[StoreElement.ApiInformation] = () => {
            this._dispatchers.apiInformation.getApiInformation();
        };
        dictionary[StoreElement.Application] = () => {
            // crappy hack that was implemented in applicationSearch, copied here with *sigh*
            // and it's buggy since we should return the Application, not the config !
            // but at the time we don't keep track of the connected application :(
            const app = this._currentApplicationService.getApplicationConfiguration();
            this._dispatchers.application.connect(app as unknown as Application);
        };
        dictionary[StoreElement.ApprovalStrategies] = () => {
            this._dispatchers.approvalStrategy.getAll();
        };
        dictionary[StoreElement.ApprovalSettings] = () => {
            this._dispatchers.approvalSettings.getAll();
        };
        dictionary[StoreElement.Categories] = () => {
            this._dispatchers.categories.getAll();
        };
        dictionary[StoreElement.ConnectionPoints] = () => {
            this._dispatchers.connectionPoints.getAll();
        };
        dictionary[StoreElement.ConnectionPointsStatuses]=()=>{
            this._dispatchers.connectionPoints.testAll();
        };

        elements.forEach(e => {
            if (dictionary[e]) {
                dictionary[e]();
            }
        });

        if (_.includes(elements, StoreElement.Dashboards)) {
            this._dispatchers.dashboards.getAll();
        }

        if (_.includes(elements, StoreElement.DashboarUsers)) {
            this._dispatchers.dashboardUsers.getAll();
        }

        if (_.includes(elements, StoreElement.AlarmEmails)) {
            this._dispatchers.dashboardUsers.getAll();
        }

        if (_.includes(elements, StoreElement.DigitalSets)) {
            this._dispatchers.digitalSets.getAll();
        }
        
        if (_.includes(elements, StoreElement.DigitalCorrespondanceTables)) {
            this._dispatchers.digitalCorrespondanceTables.getAll();
        }

        // globalNotifications, nothing to load

        // identifiers loaded through group
        if (_.includes(elements, StoreElement.Groups) || (_.includes(elements, StoreElement.Identifiers))) {
            this._dispatchers.groups.getAll();
        }

        if (_.includes(elements, StoreElement.HistorianStorageConfigurations)) {
            this._dispatchers.historianStorageConfiguration.getAll();
        }

        // identifiers loaded through groups

        // layout, nothing to load

        if (_.includes(elements, StoreElement.MailingJobs)) {
            this._dispatchers.mailingJobs.getAll();
        }

        if (_.includes(elements, StoreElement.ObjectAccessRights)) {
            this._dispatchers.objectAccessRights.getAll();
        }

        if (_.includes(elements, StoreElement.UserGroups)) {
            this._dispatchers.userGroups.getAll();
        }

        if (_.includes(elements, StoreElement.OnlineUsers)) {
            this._dispatchers.onlineUsers.getAll();
        }

        if (_.includes(elements, StoreElement.States)) {
            this._dispatchers.states.getAll();
        }

        // selectedGroup: selected by user, cannot ensure it

        // Disableed for now as dispatcher has beend commented too
        //if (_.includes(elements, StoreElement.Tasks)) {
        //    this._dispatchers.tasks.getAll();
        //}

        if (_.includes(elements, StoreElement.Templates)) {
            this._dispatchers.templates.getAll();
        }

        if (_.includes(elements, StoreElement.Tickets)) {
            this._dispatchers.tickets.getAll();
        }

        if (_.includes(elements, StoreElement.TicketStatuses)) {
            this._dispatchers.ticketStatuses.getAll();
        }

        if (_.includes(elements, StoreElement.TicketTypes)) {
            this._dispatchers.ticketTypes.getAll();
        }

        if (_.includes(elements, StoreElement.TriggerActions)) {
            this._dispatchers.triggerActions.getAll();
        }

        if (_.includes(elements, StoreElement.Triggers)) {
            this._dispatchers.triggers.getAll();
        }

        if (_.includes(elements, StoreElement.ValidationPageConfigurations)) {
            this._dispatchers.validationPageConfigurations.getAll();
        }

        if (_.includes(elements, StoreElement.ValueViewModels)) {
            this._dispatchers.valueViewModels.getAll();
        }

        if (_.includes(elements, StoreElement.Tags)) {
            this._dispatchers.tags.getAll();
        }

        if (_.includes(elements, StoreElement.Variables)) {
            this._dispatchers.variables.getVariableTypes();
            this._dispatchers.variables.getVariableSubTypes();
            this._dispatchers.variables.getQualifiers();
        }

        if (_.includes(elements, StoreElement.Users)) {
            this._dispatchers.users.getAll();
        }
    }
}